import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { CacheStatsApi } from '../../services/api/CacheStatsApi';
import { EventAggregationApi } from '../../services/api/EventAggregationApi';
import { DataHealthApi } from '../../services/api/DataHealthApi';
import { AiHealthApi } from '../../services/ai/AiHealthApi';
import { getCacheStats as getAudioCacheStats } from '../../services/AudioUrlService';
import {
  MetricCard,
  LoadingChart,
  EmptyState,
  InsightCard,
} from '../../components/analytics/SharedAnalyticsComponents';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';

const COLORS = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#17a2b8', '#6f42c1', '#20c997', '#fd7e14', '#6366f1', '#ec4899'];
const REFRESH_INTERVAL_MS = 30_000;

const cardStyle = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border-light)',
  borderRadius: 12,
  padding: '1rem 1.25rem',
  boxShadow: 'var(--shadow-sm)',
};

const sectionTitleStyle = {
  margin: '0 0 0.75rem',
  fontSize: '1rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
};

function fmtNum(n) {
  if (n == null || isNaN(n)) return '—';
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);
}

function fmtPct(v) {
  if (v == null || isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function fmtNanos(ns) {
  if (!ns || isNaN(ns)) return '—';
  if (ns < 1_000) return `${ns.toFixed(0)} ns`;
  if (ns < 1_000_000) return `${(ns / 1_000).toFixed(1)} µs`;
  if (ns < 1_000_000_000) return `${(ns / 1_000_000).toFixed(2)} ms`;
  return `${(ns / 1_000_000_000).toFixed(2)} s`;
}

function fmtDuration(ms) {
  if (ms == null || isNaN(ms)) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${(ms / 60_000).toFixed(1)} min`;
}

function relativeTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)}h ago`;
  return `${Math.round(diff / 86_400_000)}d ago`;
}

/**
 * AdminSystemStats
 *
 * Admin-only system health & cache observability dashboard. Aggregates:
 *   - Backend Caffeine cache stats (`/api/admin/cache/stats`)
 *   - Service health (Data + AI)
 *   - Aggregation pipeline status (events / GPT / payments / learning sessions)
 *   - Frontend in-browser caches (Audio URL cache, etc.)
 *
 * Auto-refreshes every 30 s, manual refresh + per-cache & global cache clear.
 */
export default function AdminSystemStats() {
  const { user, isInitialized } = useAuth();
  const isAdmin = !!user?.roles?.some((r) => r.roleName === 'admin');

  const [cacheStats, setCacheStats] = useState(null);
  const [cacheError, setCacheError] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(true);

  const [dataHealth, setDataHealth] = useState({ status: 'unknown', latencyMs: null });
  const [aiHealth, setAiHealth] = useState({ status: 'unknown', latencyMs: null });

  const [aggStatus, setAggStatus] = useState({});
  const [aggLoading, setAggLoading] = useState(true);

  const [audioCache, setAudioCache] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const loadCacheStats = useCallback(async () => {
    setCacheLoading(true);
    setCacheError(null);
    try {
      const res = await CacheStatsApi.getStats();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setCacheStats(json);
    } catch (e) {
      setCacheError(e.message || 'Failed to load cache stats');
      setCacheStats(null);
    } finally {
      setCacheLoading(false);
    }
  }, []);

  const loadHealth = useCallback(async () => {
    const probe = async (fn) => {
      const start = performance.now();
      try {
        const res = await fn();
        const latencyMs = Math.round(performance.now() - start);
        return { status: res?.ok ? 'up' : 'down', latencyMs, code: res?.status };
      } catch (e) {
        return { status: 'down', latencyMs: Math.round(performance.now() - start), error: e.message };
      }
    };
    const [dh, ah] = await Promise.all([
      probe(() => DataHealthApi.checkHealth()),
      probe(() => AiHealthApi.checkHealth()),
    ]);
    setDataHealth(dh);
    setAiHealth(ah);
  }, []);

  const loadAggregation = useCallback(async () => {
    setAggLoading(true);
    const calls = [
      ['events', EventAggregationApi.getAggregationStatus],
      ['gpt', EventAggregationApi.getGptAggregationStatus],
      ['payments', EventAggregationApi.getPaymentAggregationStatus],
      ['learningSessions', EventAggregationApi.getLearningSessionAggregationStatus],
    ];
    const results = {};
    await Promise.all(calls.map(async ([key, fn]) => {
      try {
        const res = await fn();
        if (res?.ok) {
          results[key] = await res.json();
        } else {
          results[key] = { error: `HTTP ${res?.status}` };
        }
      } catch (e) {
        results[key] = { error: e.message };
      }
    }));
    setAggStatus(results);
    setAggLoading(false);
  }, []);

  const loadAudioCache = useCallback(() => {
    try { setAudioCache(getAudioCacheStats()); } catch { setAudioCache(null); }
  }, []);

  const loadAll = useCallback(async () => {
    if (!isAdmin) return;
    await Promise.all([loadCacheStats(), loadHealth(), loadAggregation()]);
    loadAudioCache();
    setLastUpdated(new Date().toISOString());
  }, [isAdmin, loadCacheStats, loadHealth, loadAggregation, loadAudioCache]);

  useEffect(() => {
    if (!isInitialized || !isAdmin) return;
    loadAll();
    const id = setInterval(loadAll, REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isInitialized, isAdmin, loadAll]);

  const handleClearOne = async (name) => {
    if (!window.confirm(`Clear cache "${name}"? This evicts every entry now and may briefly increase backend load.`)) return;
    setActionMessage(null);
    try {
      const res = await CacheStatsApi.clearCache(name);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setActionMessage({ severity: 'success', text: `Cleared cache: ${name}` });
      loadCacheStats();
    } catch (e) {
      setActionMessage({ severity: 'danger', text: `Failed to clear ${name}: ${e.message}` });
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear ALL backend caches? This will evict every cached entry and may temporarily increase DB load.')) return;
    setActionMessage(null);
    try {
      const res = await CacheStatsApi.clearAll();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json().catch(() => ({}));
      setActionMessage({ severity: 'success', text: `Cleared ${body.clearedCount ?? 'all'} caches` });
      loadCacheStats();
    } catch (e) {
      setActionMessage({ severity: 'danger', text: `Failed to clear caches: ${e.message}` });
    }
  };

  // ── Derived values ─────────────────────────────────────────────
  const cacheRows = useMemo(() => {
    if (!cacheStats) return [];
    return Object.entries(cacheStats).map(([name, info]) => {
      const stats = info?.stats || {};
      const requests = (stats.hitCount || 0) + (stats.missCount || 0);
      return {
        name,
        size: info?.size ?? 0,
        maxSize: info?.maxSize ?? null,
        ttl: info?.ttl ?? null,
        notes: info?.notes ?? '',
        hitCount: stats.hitCount ?? 0,
        missCount: stats.missCount ?? 0,
        hitRate: stats.hitRate ?? 0,
        evictionCount: stats.evictionCount ?? 0,
        loadCount: stats.loadCount ?? 0,
        loadFailureCount: stats.loadFailureCount ?? 0,
        avgLoadPenaltyNanos: stats.avgLoadPenaltyNanos ?? 0,
        requests,
        utilization: info?.maxSize ? (info.size / info.maxSize) : null,
      };
    });
  }, [cacheStats]);

  const aggregateTotals = useMemo(() => {
    if (!cacheRows.length) return null;
    const totals = cacheRows.reduce((acc, r) => {
      acc.size += r.size;
      acc.requests += r.requests;
      acc.hits += r.hitCount;
      acc.misses += r.missCount;
      acc.evictions += r.evictionCount;
      return acc;
    }, { size: 0, requests: 0, hits: 0, misses: 0, evictions: 0 });
    totals.hitRate = totals.requests > 0 ? totals.hits / totals.requests : 0;
    return totals;
  }, [cacheRows]);

  const insights = useMemo(() => {
    const out = [];
    cacheRows.forEach((r) => {
      if (r.requests >= 100 && r.hitRate < 0.4) {
        out.push({
          severity: 'warning',
          title: `Low hit rate: ${r.name} (${fmtPct(r.hitRate)})`,
          description: `${fmtNum(r.requests)} requests with only ${fmtNum(r.hitCount)} hits. Consider increasing maxSize or TTL, or check whether the cache key includes too much variability.`,
        });
      }
      if (r.maxSize && r.utilization >= 0.95 && r.evictionCount > 0) {
        out.push({
          severity: 'warning',
          title: `Cache near capacity: ${r.name}`,
          description: `${fmtNum(r.size)} / ${fmtNum(r.maxSize)} entries (${fmtPct(r.utilization)}) with ${fmtNum(r.evictionCount)} evictions. Consider raising maxSize.`,
        });
      }
      if (r.loadFailureCount > 0) {
        out.push({
          severity: 'danger',
          title: `Load failures: ${r.name}`,
          description: `${fmtNum(r.loadFailureCount)} failed cache loads. Inspect service logs for the underlying exception.`,
        });
      }
      if (r.requests === 0 && r.size === 0) {
        out.push({
          severity: 'info',
          title: `Unused cache: ${r.name}`,
          description: 'No reads, writes, or entries observed since last JVM start. May be safe to retire.',
        });
      }
    });
    if (dataHealth.status === 'down') {
      out.push({ severity: 'danger', title: 'Data-Service unreachable', description: dataHealth.error || `Health check returned ${dataHealth.code ?? 'no response'}.` });
    }
    if (aiHealth.status === 'down') {
      out.push({ severity: 'warning', title: 'AI-Service unreachable', description: aiHealth.error || `Health check returned ${aiHealth.code ?? 'no response'}.` });
    }
    return out.slice(0, 8);
  }, [cacheRows, dataHealth, aiHealth]);

  // Bar chart data (hit rate per cache, only those with requests)
  const hitRateData = cacheRows
    .filter((r) => r.requests > 0)
    .map((r) => ({ name: r.name, hitRate: Number((r.hitRate * 100).toFixed(1)), requests: r.requests }))
    .sort((a, b) => b.requests - a.requests);

  // Pie chart data (cache size distribution)
  const sizeData = cacheRows
    .filter((r) => r.size > 0)
    .map((r) => ({ name: r.name, value: r.size }));

  // ── Guards ─────────────────────────────────────────────────────
  if (!isInitialized) {
    return <div className="settings-container"><LoadingChart height={200} /></div>;
  }
  if (!isAdmin) {
    return (
      <div className="settings-container">
        <h2>System Stats</h2>
        <InsightCard severity="warning" title="Admin access required" description="You must be signed in as an administrator to view system statistics." />
      </div>
    );
  }

  return (
    <div className="settings-container">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>System Stats</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '.8rem', color: 'var(--text-muted)' }}>
            Live cache, service-health, and aggregation pipeline observability.
            {lastUpdated && <> · Updated {relativeTime(lastUpdated)} · auto-refresh 30 s</>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button onClick={loadAll}>🔄 Refresh</button>
          <button className="secondary" onClick={handleClearAll} disabled={!cacheStats}>🗑 Clear all caches</button>
        </div>
      </div>

      {actionMessage && (
        <div style={{ marginBottom: '1rem' }}>
          <InsightCard severity={actionMessage.severity} title={actionMessage.severity === 'success' ? 'Success' : 'Action failed'} description={actionMessage.text} />
        </div>
      )}

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <MetricCard
          label="Backend Caches"
          value={cacheRows.length || '—'}
          subtitle={aggregateTotals ? `${fmtNum(aggregateTotals.size)} total entries` : 'Loading…'}
          color="#6366f1"
          loading={cacheLoading && !cacheRows.length}
        />
        <MetricCard
          label="Overall Hit Rate"
          value={aggregateTotals ? fmtPct(aggregateTotals.hitRate) : '—'}
          subtitle={aggregateTotals ? `${fmtNum(aggregateTotals.hits)} hits / ${fmtNum(aggregateTotals.requests)} req` : null}
          color={aggregateTotals && aggregateTotals.hitRate >= 0.7 ? '#28a745' : aggregateTotals && aggregateTotals.hitRate >= 0.4 ? '#ffc107' : '#dc3545'}
        />
        <MetricCard
          label="Total Evictions"
          value={aggregateTotals ? fmtNum(aggregateTotals.evictions) : '—'}
          subtitle="High = under-sized caches"
          color="#fd7e14"
        />
        <MetricCard
          label="Data-Service"
          value={dataHealth.status === 'up' ? '✅ Up' : dataHealth.status === 'down' ? '❌ Down' : '…'}
          subtitle={dataHealth.latencyMs != null ? `Health probe ${dataHealth.latencyMs} ms` : null}
          color={dataHealth.status === 'up' ? '#28a745' : '#dc3545'}
        />
        <MetricCard
          label="AI-Service"
          value={aiHealth.status === 'up' ? '✅ Up' : aiHealth.status === 'down' ? '❌ Down' : '…'}
          subtitle={aiHealth.latencyMs != null ? `Health probe ${aiHealth.latencyMs} ms` : null}
          color={aiHealth.status === 'up' ? '#28a745' : '#dc3545'}
        />
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h3 style={sectionTitleStyle}>Insights & alerts</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '0.75rem' }}>
            {insights.map((ins, i) => (
              <InsightCard key={i} severity={ins.severity} title={ins.title} description={ins.description} />
            ))}
          </div>
        </div>
      )}

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(360px,1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>Hit rate by cache (%)</h3>
          {cacheLoading ? <LoadingChart height={260} /> : hitRateData.length === 0 ? (
            <EmptyState icon="📈" message="No cache traffic recorded yet." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={hitRateData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-25} textAnchor="end" interval={0} height={60} />
                <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                <Tooltip formatter={(v, k) => k === 'hitRate' ? `${v}%` : fmtNum(v)} />
                <Bar dataKey="hitRate" name="Hit rate">
                  {hitRateData.map((d, i) => (
                    <Cell key={i} fill={d.hitRate >= 70 ? '#28a745' : d.hitRate >= 40 ? '#ffc107' : '#dc3545'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>Cache entries distribution</h3>
          {cacheLoading ? <LoadingChart height={260} /> : sizeData.length === 0 ? (
            <EmptyState icon="🗂" message="No cached entries yet." />
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={sizeData} dataKey="value" nameKey="name" outerRadius={90} label={(e) => e.name}>
                  {sizeData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtNum(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Cache table */}
      <div style={{ ...cardStyle, marginBottom: '1.5rem', overflowX: 'auto' }}>
        <h3 style={sectionTitleStyle}>Backend cache details</h3>
        {cacheError && <InsightCard severity="danger" title="Failed to load cache stats" description={cacheError} />}
        {cacheLoading && !cacheRows.length ? <LoadingChart height={200} /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-light)' }}>
                <th style={{ padding: '6px 8px' }}>Cache</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Size / Max</th>
                <th style={{ padding: '6px 8px' }}>TTL</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Requests</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Hit rate</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Evictions</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Avg load</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Failures</th>
                <th style={{ padding: '6px 8px' }}></th>
              </tr>
            </thead>
            <tbody>
              {cacheRows.map((r) => (
                <tr key={r.name} style={{ borderBottom: '1px solid var(--border-light)' }} title={r.notes}>
                  <td style={{ padding: '6px 8px', fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtNum(r.size)}{r.maxSize ? ` / ${fmtNum(r.maxSize)}` : ''}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-muted)' }}>{r.ttl || '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtNum(r.requests)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: r.requests === 0 ? 'var(--text-muted)' : r.hitRate >= 0.7 ? '#28a745' : r.hitRate >= 0.4 ? '#ffc107' : '#dc3545', fontWeight: 600 }}>
                    {r.requests === 0 ? '—' : fmtPct(r.hitRate)}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtNum(r.evictionCount)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtNanos(r.avgLoadPenaltyNanos)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: r.loadFailureCount > 0 ? '#dc3545' : 'var(--text-muted)' }}>{fmtNum(r.loadFailureCount)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    <button className="secondary" style={{ fontSize: '.7rem', padding: '2px 6px' }} onClick={() => handleClearOne(r.name)}>Clear</button>
                  </td>
                </tr>
              ))}
              {!cacheRows.length && !cacheLoading && (
                <tr><td colSpan={9}><EmptyState icon="🗂" message="No caches reported." /></td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Aggregation status */}
      <div style={{ ...cardStyle, marginBottom: '1.5rem', overflowX: 'auto' }}>
        <h3 style={sectionTitleStyle}>Aggregation pipelines</h3>
        {aggLoading ? <LoadingChart height={160} /> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-light)' }}>
                <th style={{ padding: '6px 8px' }}>Pipeline</th>
                <th style={{ padding: '6px 8px' }}>Last aggregated</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Pending</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Last duration</th>
                <th style={{ padding: '6px 8px' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(aggStatus).map(([key, s]) => {
                const last = s?.lastAggregatedAt || s?.lastCheckpoint || s?.checkpoint || s?.lastRunAt;
                const pending = s?.pendingRecords ?? s?.pendingCount ?? s?.pending ?? null;
                const duration = s?.lastDurationMs ?? s?.lastRunDurationMs ?? null;
                const error = s?.error;
                return (
                  <tr key={key} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{key}</td>
                    <td style={{ padding: '6px 8px' }}>{last ? `${relativeTime(last)} · ${new Date(last).toLocaleString()}` : '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right', color: pending > 1000 ? '#dc3545' : pending > 100 ? '#ffc107' : 'var(--text-primary)' }}>{pending != null ? fmtNum(pending) : '—'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtDuration(duration)}</td>
                    <td style={{ padding: '6px 8px', color: error ? '#dc3545' : '#28a745', fontWeight: 600 }}>
                      {error ? `❌ ${error}` : '✅ ok'}
                    </td>
                  </tr>
                );
              })}
              {Object.keys(aggStatus).length === 0 && (
                <tr><td colSpan={5}><EmptyState icon="⚙️" message="No aggregation status reported." /></td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Frontend in-browser caches */}
      <div style={cardStyle}>
        <h3 style={sectionTitleStyle}>Frontend in-browser cache</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '1rem' }}>
          <MetricCard
            label="Audio URL cache"
            value={fmtNum(audioCache?.size ?? 0)}
            subtitle={audioCache?.entries?.length ? `${audioCache.entries.filter(e => e.isValid).length} valid entries` : 'No cached URLs'}
            color="#17a2b8"
          />
        </div>
        {audioCache?.entries?.length > 0 && (
          <div style={{ marginTop: '0.75rem', maxHeight: 220, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.72rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-light)' }}>
                  <th style={{ padding: '4px 8px' }}>Document</th>
                  <th style={{ padding: '4px 8px' }}>Valid</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Expires in</th>
                </tr>
              </thead>
              <tbody>
                {audioCache.entries.slice(0, 50).map((e, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td style={{ padding: '4px 8px' }}>{e.documentId}</td>
                    <td style={{ padding: '4px 8px', color: e.isValid ? '#28a745' : '#dc3545' }}>{e.isValid ? '✅' : '❌'}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>{e.minutesUntilExpiry != null ? `${e.minutesUntilExpiry} min` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
